import { useState } from "react";
import {
  createStaffColumn,
  deleteStaffColumn,
  updateStaffColumn,
} from "@/api/staffColumns";
import { ApiError } from "@/api/client";
import { IconCheck, IconPlus, IconTrash } from "@/components/icons";
import {
  STAFF_COLUMNS_LIMIT,
  STAFF_COLUMN_TYPE_OPTIONS,
  staffColumnTypeHint,
  staffColumnTypeLabel,
  uniqueStaffColumnName,
} from "@/lib/forms/staffColumns";
import type { StaffColumnDto, StaffColumnType } from "@/types/staffColumn";

function choicesToText(column: StaffColumnDto): string {
  return column.options?.choices?.join("\n") ?? "";
}

function parseChoices(raw: string): string[] {
  return raw
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function StaffColumnsSettings({
  formId,
  columns,
  onColumnsChange,
  canManage = true,
  selectedIds,
  onToggleSelected,
}: {
  formId: number;
  columns: StaffColumnDto[];
  onColumnsChange: (columns: StaffColumnDto[]) => void;
  canManage?: boolean;
  selectedIds?: Set<number>;
  onToggleSelected?: (columnId: number) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [draftNames, setDraftNames] = useState<Record<number, string>>({});
  const [draftChoices, setDraftChoices] = useState<Record<number, string>>({});

  const atLimit = columns.length >= STAFF_COLUMNS_LIMIT;

  const applyColumns = (next: StaffColumnDto[]) => {
    const sorted = [...next].sort(
      (left, right) => left.order - right.order || left.id - right.id,
    );
    onColumnsChange(sorted);
    setDraftNames(
      Object.fromEntries(sorted.map((item) => [item.id, item.name])),
    );
    setDraftChoices(
      Object.fromEntries(sorted.map((item) => [item.id, choicesToText(item)])),
    );
  };

  const applyColumn = (column: StaffColumnDto) => {
    const exists = columns.some((item) => item.id === column.id);
    applyColumns(
      exists
        ? columns.map((item) => (item.id === column.id ? column : item))
        : [...columns, column],
    );
  };

  const handleAddType = async (type: StaffColumnType) => {
    if (!canManage || atLimit || adding) return;
    const preset = STAFF_COLUMN_TYPE_OPTIONS.find((item) => item.type === type);
    if (!preset) return;
    setAdding(true);
    setError(null);
    try {
      const column = await createStaffColumn(formId, {
        name: uniqueStaffColumnName(columns, preset.name),
        type: preset.type,
        options: preset.options,
        order: columns.length + 1,
      });
      applyColumn(column);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось создать столбец",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleAddPresets = async () => {
    if (!canManage || adding || atLimit) return;
    setAdding(true);
    setError(null);
    try {
      let order = columns.length;
      let nextColumns = [...columns];
      for (const preset of STAFF_COLUMN_TYPE_OPTIONS.filter(
        (item) => item.type === "status" || item.type === "text",
      )) {
        if (order >= STAFF_COLUMNS_LIMIT) break;
        order += 1;
        const column = await createStaffColumn(formId, {
          name: uniqueStaffColumnName(nextColumns, preset.name),
          type: preset.type,
          options: preset.options,
          order,
        });
        nextColumns = [...nextColumns, column];
      }
      applyColumns(nextColumns);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось добавить статус и комментарий",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async (column: StaffColumnDto) => {
    if (!canManage) return;
    const nextName = (draftNames[column.id] ?? column.name).trim();
    if (!nextName || nextName === column.name) return;
    setBusyId(column.id);
    setError(null);
    try {
      applyColumn(
        await updateStaffColumn(formId, column.id, { name: nextName }),
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось переименовать столбец",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveChoices = async (column: StaffColumnDto) => {
    if (!canManage) return;
    const choices = parseChoices(draftChoices[column.id] ?? "");
    setBusyId(column.id);
    setError(null);
    try {
      applyColumn(
        await updateStaffColumn(formId, column.id, { options: { choices } }),
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось сохранить статусы",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (column: StaffColumnDto) => {
    if (!canManage || busyId != null) return;
    if (
      !window.confirm(`Удалить столбец «${column.name}» и все отметки в нём?`)
    ) {
      return;
    }
    setBusyId(column.id);
    setError(null);
    try {
      await deleteStaffColumn(formId, column.id);
      applyColumns(columns.filter((item) => item.id !== column.id));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось удалить столбец",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {error ? (
        <p className="mb-2 font-onest text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}

      {columns.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/70 px-3 py-3">
          <p className="font-onest text-sm text-slate-600">
            Пометок пока нет. Это ваши столбцы в таблице: респондент их не
            видит.
          </p>
          {canManage ? (
            <button
              type="button"
              disabled={adding}
              onClick={() => void handleAddPresets()}
              className="mt-2 font-onest text-sm font-medium text-brand-600 hover:text-brand-500 disabled:opacity-50"
            >
              {adding ? "Добавление…" : "Добавить статус и комментарий"}
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="space-y-2">
          {columns.map((column) => {
            const selected = selectedIds?.has(column.id) ?? true;
            return (
              <li
                key={column.id}
                className="rounded-lg border border-slate-200/80 px-2.5 py-2"
              >
                <div className="flex items-start gap-2">
                  {onToggleSelected ? (
                    <button
                      type="button"
                      onClick={() => onToggleSelected(column.id)}
                      className="mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center"
                      aria-label={
                        selected
                          ? `Скрыть столбец «${column.name}»`
                          : `Показать столбец «${column.name}»`
                      }
                    >
                      {selected ? (
                        <IconCheck size={16} className="text-brand-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-sm border border-slate-300" />
                      )}
                    </button>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    {canManage ? (
                      <input
                        value={draftNames[column.id] ?? column.name}
                        disabled={busyId === column.id}
                        aria-label={`Название столбца «${column.name}»`}
                        onChange={(event) =>
                          setDraftNames((current) => ({
                            ...current,
                            [column.id]: event.target.value,
                          }))
                        }
                        onBlur={() => void handleRename(column)}
                        className="w-full rounded-md border border-slate-200 px-2 py-1 font-onest text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-slate-50"
                      />
                    ) : (
                      <p className="font-onest text-sm text-slate-800">
                        {column.name}
                      </p>
                    )}
                    <p className="mt-0.5 font-onest text-[11px] text-slate-400">
                      {staffColumnTypeLabel(column.type)}
                      {staffColumnTypeHint(column.type)
                        ? ` · ${staffColumnTypeHint(column.type)}`
                        : ""}
                    </p>
                  </div>
                  {canManage ? (
                    <button
                      type="button"
                      disabled={busyId != null}
                      title="Удалить столбец"
                      onClick={() => void handleDelete(column)}
                      className="mt-1 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >
                      <IconTrash size={14} />
                    </button>
                  ) : null}
                </div>
                {column.type === "status" ? (
                  canManage ? (
                    <textarea
                      value={draftChoices[column.id] ?? choicesToText(column)}
                      disabled={busyId === column.id}
                      rows={3}
                      onChange={(event) =>
                        setDraftChoices((current) => ({
                          ...current,
                          [column.id]: event.target.value,
                        }))
                      }
                      onBlur={() => void handleSaveChoices(column)}
                      className="mt-2 w-full rounded-lg border border-slate-200 px-2 py-1.5 font-onest text-xs text-slate-800"
                      placeholder={"новый\nв работе\nготово"}
                      aria-label={`Варианты статуса «${column.name}»`}
                    />
                  ) : (
                    <p className="mt-1.5 font-onest text-xs text-slate-500">
                      {(column.options?.choices ?? []).join(" · ") ||
                        "Нет вариантов"}
                    </p>
                  )
                ) : null}
                {column.type === "status" && canManage ? (
                  <p className="mt-1 font-onest text-[11px] text-slate-400">
                    Каждый вариант с новой строки
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {canManage && !atLimit ? (
        <div className="mt-3">
          <p className="mb-1.5 font-onest text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Добавить столбец
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {STAFF_COLUMN_TYPE_OPTIONS.map((item) => (
              <button
                key={item.type}
                type="button"
                disabled={adding}
                onClick={() => void handleAddType(item.type)}
                className="flex items-start gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left hover:border-brand-200 hover:bg-brand-50/40 disabled:opacity-50"
              >
                <IconPlus
                  size={12}
                  className="mt-0.5 shrink-0 text-slate-400"
                />
                <span>
                  <span className="block font-onest text-xs font-medium text-slate-800">
                    {item.label}
                  </span>
                  <span className="block font-onest text-[11px] leading-4 text-slate-500">
                    {item.hint}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {canManage && atLimit ? (
        <p className="mt-2 font-onest text-xs text-slate-500">
          Не больше {STAFF_COLUMNS_LIMIT} столбцов пометок.
        </p>
      ) : null}
    </div>
  );
}
